import { useCallback, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkbox } from '@cbhq/cds-web/controls/Checkbox';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography/Text';

import type { MemberRole, MemberRoleMap } from '../../utils/constants';

import type { ProductIconName } from './ProductIcon';
import { ProductIcon } from './ProductIcon';
import { ProductRolePopover } from './ProductRolePopover';

const messages = defineMessages({
  multipleRolesSummary: {
    id: 'Team.inviteTray.multipleRolesSummary',
    defaultMessage: '{count} roles',
    description: 'Summary label shown on a product row when multiple roles are selected',
  },
  noAccessSummary: {
    id: 'Team.inviteTray.noAccessSummary',
    defaultMessage: 'No access',
    description: 'Summary label shown on a product row when no roles are selected',
  },
});

export type ProductAccessRowConfig = {
  productKey: string;
  iconName: ProductIconName;
  iconBackground: string;
  title: string;
  description: string;
  onCheckedChange?: (checked: boolean) => void;
  /**
   * All roles for the product. Unassignable held roles (`assignable === false`)
   * render checked + disabled so an admin-only member reads as having access.
   */
  roleOptions: MemberRoleMap[];
  selectedRoleValues: MemberRole[];
  onRoleToggle: (value: MemberRole, isSelected: boolean) => void;
  isEditable?: boolean;
};

export function ProductAccessRow({
  productKey,
  iconName,
  iconBackground,
  title,
  description,
  onCheckedChange,
  roleOptions,
  selectedRoleValues,
  onRoleToggle,
  isEditable = true,
}: ProductAccessRowConfig) {
  const { formatMessage } = useIntl();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onCheckedChange?.(event.target.checked);
    },
    [onCheckedChange],
  );

  // The roles the member holds for this product. `assignable` only governs
  // editability: unassignable held roles are seeded into the selection upstream,
  // so they show here as checked but locked.
  const checkedRoles = useMemo(
    () => roleOptions.filter((role) => selectedRoleValues.includes(role.value)),
    [roleOptions, selectedRoleValues],
  );
  const hasUnassignableRole = checkedRoles.some((role) => role.assignable === false);
  const checked = checkedRoles.length > 0;

  const roleLabel = useMemo(() => {
    if (checkedRoles.length > 1) {
      return formatMessage(messages.multipleRolesSummary, { count: checkedRoles.length });
    }
    if (checkedRoles.length === 0) {
      return formatMessage(messages.noAccessSummary);
    }
    return checkedRoles[0].name;
  }, [formatMessage, checkedRoles]);

  return (
    <HStack width="100%" alignItems="center" gap={2}>
      <HStack flexGrow={1} alignItems="center" gap={2}>
        <Checkbox
          testID={`team-product-access-checkbox-${productKey}`}
          value={productKey}
          accessibilityLabel={title}
          checked={checked}
          onChange={handleChange}
          // Lock the checkbox when the member holds an unassignable role — it
          // can't be removed from this tray.
          disabled={!isEditable || hasUnassignableRole}
          controlSize={22}
          borderRadius={0}
          background={checked ? 'fg' : undefined}
          borderColor={checked ? 'fg' : undefined}
        />
        <HStack flexGrow={1} alignItems="center" gap={1.5}>
          <ProductIcon name={iconName} background={iconBackground} />
          <VStack gap={0} alignItems="flex-start" flexGrow={1}>
            <Text as="span" font="label1" color="fg" noWrap>
              {title}
            </Text>
            <Text as="span" font="label2" color="fgMuted" noWrap>
              {description}
            </Text>
          </VStack>
        </HStack>
      </HStack>
      <ProductRolePopover
        roleOptions={roleOptions}
        selectedRoleValues={selectedRoleValues}
        onRoleToggle={onRoleToggle}
        roleLabel={roleLabel}
        productLabel={title}
        disabled={roleOptions.length === 0 || !isEditable}
      />
    </HStack>
  );
}
